import { faPrint, faSpinnerThird, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import AbiEntrySummaryApiUrls from "src/apiUrls/AbiEntrySummaryApiUrls"
import RenderFormFields from 'src/components/era-package/forms/RenderFormFields'
import { optionsMap as optionsMap3461, print3461Form } from 'src/views/abi/fields/print-3461-form'
import { optionsMap as optionsMap7501, print7501Form } from 'src/views/abi/fields/print-7501-form'

export const FormType = {
  FORM_7501: {
    name: 'Form 7501',
    get: AbiEntrySummaryApiUrls.GET_FORM_7501_PRINT_OPTIONS,
    post: AbiEntrySummaryApiUrls.DOWNLOAD_FORM_7501,
    fields: print7501Form,
    optionsMap: optionsMap7501,
  },
  FORM_3461: {
    name: 'Form 3461',
    get: AbiEntrySummaryApiUrls.GET_FORM_3461_PRINT_OPTIONS,
    post: AbiEntrySummaryApiUrls.DOWNLOAD_FORM_3461,
    fields: print3461Form,
    optionsMap: optionsMap3461,
  },
}

export const PrintDialog = ({ formType, transId, onPrint, onClose, ...props }) => {

  const [data, setData] = useState({})
  const [printing, setPrinting] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const form = FormType[formType]

  useEffect(() => {
    let isMounted = true

    if (!formType || !transId || !FormType.hasOwnProperty(formType)) {
      if (isMounted) {
        setData({}) // reset data if form type or transaction ID is invalid
        onClose(false)
      }
      return
    }

    const fetchData = async () => {
      try {
        const response = await fetch(`${form.get}?abiTransId=${transId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
          },
        })
        const json = await response.json()
        if (isMounted && response.status === 200) {
          setData(json.data)
        } else if (response.status !== 200) {
          // enqueueSnackbar(json?.message || `Error fetching print options for ${form.name}`, { variant: 'error' })
          throw new Error(json?.message || `Error fetching print options for ${form.name}`)
        }
      } catch (error) {
        console.error('Error generating form data:', error)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, form, transId])

  const handlePrint = async () => {
    setPrinting(true)
    const formData = new FormData()
    formData.append('abiTransId', transId)
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value)
    })

    const response = await fetch(form.post, {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: formData
    })

    if (!response.ok) {
      setPrinting(false)
      const responseBody = await response.json() // Parse the JSON body
      enqueueSnackbar(responseBody?.message || `Error generating ${form.name}`, { variant: 'error' })
      return
    }

    // Extract filename from headers
    const contentDisposition = response.headers.get('Content-Disposition')
    let fileName = form.name.replace(/[^a-zA-Z0-9]+/g, "_")
      + '_' + transId + '.pdf'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1])
      }
    }

    // Convert response to blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    setPrinting(false)
    onClose('success')
  }

  return <Dialog open={FormType.hasOwnProperty(formType)} onClose={() => onClose(false)}>
    <DialogTitle
      id="print-options-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      {form?.name} Print Options
      <IconButton
        onClick={() => onClose(false)}
        sx={{ width: 40, height: 40, color: 'grey.500' }}
        title="Close print options"
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
    </DialogTitle>

    <DialogContent id="print-options-content"
      sx={{ px: 3, }}
    >
      <RenderFormFields
        fields={form?.fields || []}
        optionsMap={form?.optionsMap}
        formData={data}
        setFormData={setData}
      />
    </DialogContent>

    <DialogActions>
      <Button fullWidth variant='contained'
        size='large'
        onClick={handlePrint}
        disabled={printing}
        startIcon={<FontAwesomeIcon
          icon={printing ? faSpinnerThird : faPrint}
          spin={printing}
        />}
        title="Generate Form 7501">
        Print
      </Button>
    </DialogActions>
  </Dialog>
}